import React, { useEffect, useState, useCallback, useRef } from "react";
// @material-ui/core components
import { useHistory } from "react-router-dom";

import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Backdrop,
} from "@material-ui/core";
// core components
import GridItem from "../../components/Grid/GridItem.js";
import GridContainer from "../../components/Grid/GridContainer.js";
// import Table from "../../components/Table/Table.js";
import Card from "../../components/Card/Card.js";
import CardHeader from "../../components/Card/CardHeader.js";
import CardBody from "../../components/Card/CardBody.js";
import CustomInput from "../../components/CustomInput/CustomInput";
import People from "@material-ui/icons/People";
import VisibilityIcon from "@material-ui/icons/Visibility";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import PrintIcon from "@mui/icons-material/Print";
import AddIcon from "@material-ui/icons/Add";
import AccountAdd from "./AccountAdd";
import AccountView from "./AccountView";
import _ from "lodash";
import { ConfirmationDialog } from "../../components/common";
import AccountEdit from "./AccountEdit";
import axios from "axios";
import {
  DeleteAccountAPI,
  GetAllAccountsAPI,
  GetEntityAPI,
  GetAccountAPI,
} from "../../config/api";
import { Close, CloudDownload, CloudUpload, Search } from "@material-ui/icons";
import { ImportExcel } from "../Master/ImportExcel";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as XLSX from 'xlsx';
const useStyles = makeStyles((theme) => ({
  fab: {
    position: "absolute",
    bottom: theme.spacing(2),
    right: theme.spacing(2),
  },
  cardCategoryWhite: {
    "&,& a,& a:hover,& a:focus": {
      color: "rgba(255,255,255,.62)",
      margin: "0",
      fontSize: "14px",
      marginTop: "0",
      marginBottom: "0",
    },
    "& a,& a:hover,& a:focus": {
      color: "#FFFFFF",
    },
  },
  cardTitleWhite: {
    color: "#FFFFFF",
    marginTop: "0px",
    minHeight: "auto",
    fontWeight: "300",
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    marginBottom: "3px",
    textDecoration: "none",
    "& small": {
      color: "#777",
      fontSize: "65%",
      fontWeight: "400",
      lineHeight: "1",
    },
  },
}));
const columns = [
  { id: "name", label: "Name" },
  { id: "accountCode", label: "Account Code" },
  { id: "whatsappNumber", label: "whatsapp Number" },
  { id: "city", label: "City" },
  { id: "state", label: "State" },
  { id: "status", label: "Status" },
  { id: "action", label: "Action" },
];

export default function TableList() {
  const classes = useStyles();
  const history = useHistory();

  const [accounts, setAccounts] = useState([]);
  const [orderBy, setOrderBy] = useState("name");
  const [order, setOrder] = useState("asc");
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(null);
  const [viewAccountData, setViewAccountData] = useState(null);
  const [loadingViewData, setLoadingViewData] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [search, setSearch] = useState("");
  const [openConfirmationModal, setOpenConfirmationModal] = useState(null);
  const [openImportDialog, setOpenImportDialog] = useState(false);
  const [entity, setEntity] = useState("");
  const [tota, setTota] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const authToken = useSelector((state) => state.userReducer.authToken);
  const userInfo = useSelector((state) => state.userReducer.userInfo);

  const hasInitialLoad = useRef(false); // Ref to track if initial load has happened
  const isLoadingRef = useRef(false); // Ref to prevent duplicate API calls

  const _getAccounts = useCallback(async (pageNum, rowsPerPageNum, search = "") => {
    // Prevent duplicate calls
    if (isLoadingRef.current) {
      return;
    }

    try {
      isLoadingRef.current = true;
      setLoading(true);
      const skip = pageNum * rowsPerPageNum;
      let query = `&limit=${rowsPerPageNum}&skip=${skip}`;

      if (search) {
        query += `&search=${encodeURIComponent(search)}`;
      }

      const res = await axios.get(
        GetAllAccountsAPI(userInfo.entity) + query,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      // Remove client-side sorting - backend should handle this
      setAccounts(res.data.accounts || []);
      setTota(res.data.total || 0);
    } catch (err) {
      toast.error("Something went wrong, Please Contact Admin", {
        position: "top-center",
        autoClose: 1500,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
      setAccounts([]);
      setTota(0);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [userInfo.entity, authToken]);

  // Initial load: only on mount
  useEffect(() => {
    let isMounted = true;
    const loadData = async () => {
      await _getAccounts(0, rowsPerPage, "");
      if (isMounted) {
        hasInitialLoad.current = true; // Mark initial load as complete AFTER API call
      }
    };
    loadData();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Debounced search effect: only runs when search input changes (not on initial mount)
  useEffect(() => {
    // Skip on initial mount to prevent duplicate call
    if (!hasInitialLoad.current) {
      return;
    }

    const timeoutId = setTimeout(() => {
      setSearchTerm(search);
      setPage(0); // Reset to first page on new search
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [search]);

  // Fetch when searchTerm changes (after debounce) OR when pagination changes
  useEffect(() => {
    // Skip initial mount (already handled above)
    if (!hasInitialLoad.current) {
      return;
    }
    _getAccounts(page, rowsPerPage, searchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchTerm]); // Pagination and search changes trigger this

  // Function to refresh data (called explicitly after successful operations)
  const refreshAccounts = useCallback(() => {
    _getAccounts(page, rowsPerPage, searchTerm);
  }, [page, rowsPerPage, searchTerm, _getAccounts]);


  // Removed _getEntity - only fetch when needed (e.g., in AccountView or print functionality)

  // Export function using XLSX
  const handleExport = async () => {
    setIsExporting(true);
    const loadingToastId = toast.loading('🔄 Preparing export... Please wait', {
      position: "top-center",
      autoClose: false,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: true,
      draggable: false,
      progress: undefined,
      theme: "colored",
    });

    try {
      // First, get the total count
      toast.update(loadingToastId, {
        render: '📊 Fetching total record count...',
        type: 'info',
        isLoading: true,
      });

      const totalRes = await axios.get(
        GetAllAccountsAPI(userInfo.entity) + `&limit=1&skip=0`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      const total = totalRes.data.total || 0;
      
      if (total === 0) {
        toast.update(loadingToastId, {
          render: '❌ No data to export!',
          type: 'error',
          isLoading: false,
          autoClose: 3000,
        });
        setIsExporting(false);
        return;
      }

      // Fetch all data using the total count
      toast.update(loadingToastId, {
        render: `📥 Downloading ${total} records... This may take a moment`,
        type: 'info',
        isLoading: true,
      });

      const res = await axios.get(
        GetAllAccountsAPI(userInfo.entity) + `&limit=${total}&skip=0`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      const allData = res.data.accounts || [];
      if (!allData || allData.length === 0) {
        toast.update(loadingToastId, {
          render: '❌ No data to export!',
          type: 'error',
          isLoading: false,
          autoClose: 3000,
        });
        setIsExporting(false);
        return;
      }

      toast.update(loadingToastId, {
        render: '⚙️ Processing data for export...',
        type: 'info',
        isLoading: true,
      });

      const exportHeadings = [
        { id: "accountCode", label: "Account Code" },
        { id: "partyType", label: "Party Type" },
        { id: "name", label: "Name" },
        { id: "contact", label: "Contact" },
        { id: "address", label: "Address" },
        { id: "city", label: "City" },
        { id: "state", label: "State" },
        { id: "pin", label: "PIN" },
        { id: "landline", label: "Landline" },
        { id: "whatsappNumber", label: "WhatsApp Number" },
        { id: "mobile", label: "Mobile" },
        { id: "email", label: "Email" },
        { id: "shippingAddress", label: "Shipping Address" },
        { id: "openBalance", label: "Open Balance" },
        { id: "currentBalance", label: "Current Balance" },
        { id: "lastYearBalance", label: "Last Year Balance" },
        { id: "thresholdBalance", label: "Threshold Balance" },
        { id: "challanType", label: "Challan Type" },
        { id: "cashPreference", label: "Cash Preference" },
        { id: "showRunningBalance", label: "Show Running Balance" },
        { id: "runningChallanBalance", label: "Running Challan Balance" },
        { id: "runningBillBalance", label: "Running Bill Balance" },
        { id: "runningPurchaseBalance", label: "Running Purchase Balance" }
      ];

      const exportData = allData.map(row => {
        const rowData = {};
        exportHeadings.forEach(col => {
          rowData[col.label] = row[col.id] || "";
        });
        return rowData;
      });

      toast.update(loadingToastId, {
        render: '💾 Generating Excel file...',
        type: 'info',
        isLoading: true,
      });

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Accounts');
      XLSX.writeFile(workbook, 'accounts_export.xlsx');

      toast.update(loadingToastId, {
        render: `✅ Successfully exported ${allData.length} records to Excel!`,
        type: 'success',
        isLoading: false,
        autoClose: 4000,
        style: {
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: '#fff',
          fontWeight: 'bold',
          fontSize: '14px',
        },
      });
    } catch (error) {
      console.error('Export error:', error);
      toast.update(loadingToastId, {
        render: `❌ Export failed: ${error.response?.data?.message || error.message || 'Unknown error'}`,
        type: 'error',
        isLoading: false,
        autoClose: 5000,
        style: {
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          color: '#fff',
          fontWeight: 'bold',
          fontSize: '14px',
        },
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Export import template
  const handleExportTemplate = () => {
    const templateHeadings = [
      "accountCode",
      "schedNo",
      "name",
      "contact",
      "address",
      "city",
      "state",
      "pin",
      "postAccount",
      "officeTelephone",
      "residentTelephone",
      "fax",
      "mobile",
      "email",
      "shippingAddress",
      "openBalance",
      "currentBalance",
      "lastYearBalance"
    ];
    
    const templateData = [templateHeadings];
    const worksheet = XLSX.utils.aoa_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Import Template');
    XLSX.writeFile(workbook, 'accounts_import_template.xlsx');
    toast.success('Import template downloaded!', {
      position: "top-center",
      autoClose: 2000,
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };
  const handleOpenConfirmationModal = (object) => {
    setOpenConfirmationModal(object);
  };
  const handleCloseConfirmationModal = () => {
    setOpenConfirmationModal(null);
  };

  function closeAddDialog() {
    setOpenAddDialog(false);
    // Don't refresh here - AccountAdd will call fetchData after successful save
  }

  function closeViewDialog() {
    setOpenViewDialog(null);
  }

  async function _deleteItem(item) {
    try {
      await axios.delete(DeleteAccountAPI(item._id), {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      toast.success('Successfully Deleted!', {
        position: "top-center",
        autoClose: 1500,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
      refreshAccounts(); // Refresh data after delete
    } catch (e) {
      toast.error("Something went wrong, Please Contact Admin", {
        position: "top-center",
        autoClose: 1500,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      });
    }
  }

  async function _printItem(item) {
    try {
      const [entityRes, accountRes] = await Promise.all([
        axios.get(GetEntityAPI(userInfo.entity), {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }),
        axios.get(GetAccountAPI(item._id), {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }),
      ]);
      history.push("/admin/account-address", {
        account: accountRes.data,
        sender: entityRes.data,
      });
    } catch (err) {
      toast.error("Failed to load account or entity for printing", {
        position: "top-center",
        autoClose: 1500,
      });
    }
  }

  // Removed client-side filtering - server handles search
  // Removed handleRequestSort - server should handle sorting if needed
  // Removed handleSearch - using debounced searchTerm directly


  return (
    <GridContainer>
      <GridItem xs={12} sm={12} md={12}>
        <Card>
          <CardHeader color="primary">
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h4 className={classes.cardTitleWhite}>Accounts</h4>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-evenly",
                  alignItems: "center",
                }}
              >
                <CustomInput
                  id="search"
                  formControlProps={{
                    fullWidth: true,
                  }}
                  inputProps={{
                    style: {
                      color: "white",
                    },
                    placeholder: "Search",
                    value: search,
                    onChange: (e) => {
                      setSearch(e.target.value); // Only update search, debounced effect will handle searchTerm
                    },
                    endAdornment: (
                      <InputAdornment position="end">
                        {searchTerm ? (
                          <IconButton
                            color={"secondary"}
                            onClick={() => {
                              setSearch("");
                              setSearchTerm("");
                            }}
                          >
                            <Close />
                          </IconButton>
                        ) : (
                          <IconButton color={"secondary"}>
                            <Search />
                          </IconButton>
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
                <IconButton
                  aria-label="add"
                  style={{ color: "white" }}
                  onClick={() => setOpenAddDialog(true)}
                >
                  <AddIcon />
                </IconButton>
              </div>
            </div>
          </CardHeader>

          <CardBody>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={"center"}
                      style={{ minWidth: column.minWidth }}
                    >
                      <Box
                        display={"flex"}
                        justifyContent={"center"}
                        alignItems={"center"}
                      >
                        {column.label}
                        {column.id === "action" ? (
                          <Box justifyContent={"center"} alignItems={"center"}>
                            <Tooltip title={"Export to excel"}>
                              <IconButton
                                color={"primary"}
                                onClick={handleExport}
                                disabled={isExporting}
                              >
                                <CloudDownload />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={"Download Import Template"}>
                              <IconButton
                                color={"primary"}
                                onClick={handleExportTemplate}
                              >
                                <CloudDownload />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={"Import Excel to database"}>
                              <IconButton
                                color={"primary"}
                                onClick={() => setOpenImportDialog(true)}
                              >
                                <CloudUpload />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : null}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center" style={{ padding: "40px" }}>
                      <CircularProgress />
                      <Box style={{ color: "#666", marginTop: "16px" }}>
                        {searchTerm ? "Searching accounts..." : "Loading accounts..."}
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : accounts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center" style={{ padding: "40px" }}>
                      <Box style={{ color: "#666" }}>
                        {searchTerm ? "No accounts found matching your search." : "No accounts found."}
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  accounts.map((row) => {
                    return (
                      <TableRow
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        key={row._id}
                      >
                        {columns.map((column) => {
                          const value = row[column.id];
                          if (column.id === "action") {
                            return (
                              <TableCell align={"center"}>
                                <Box display={"flex"} justifyContent={"center"}>
                                  <IconButton
                                    onClick={async () => {
                                      if (row && row._id) {
                                        setLoadingViewData(true);
                                        try {
                                          // Fetch full account data for view
                                          const res = await axios.get(GetAccountAPI(row._id), {
                                            headers: {
                                              Authorization: `Bearer ${authToken}`,
                                            },
                                          });
                                          setViewAccountData(res.data);
                                          setOpenViewDialog({
                                            open: true,
                                            item: res.data,
                                          });
                                        } catch (err) {
                                          toast.error("Failed to load account details", {
                                            position: "top-center",
                                            autoClose: 2000,
                                          });
                                        } finally {
                                          setLoadingViewData(false);
                                        }
                                      } else {
                                        toast.error("Unable to view account details");
                                      }
                                    }}
                                    aria-label="view"
                                    disabled={loadingViewData}
                                  >
                                    <VisibilityIcon />
                                  </IconButton>
                                  <IconButton
                                    aria-label="edit"
                                    onClick={() => {
                                      setSelectedItem(row);
                                      setOpenEditDialog(true);
                                    }}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                  <IconButton
                                    onClick={() =>
                                      handleOpenConfirmationModal({
                                        onPress: () => _deleteItem(row),
                                      })
                                    }
                                    aria-label="delete"
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                  <IconButton
                                    onClick={() =>
                                      _printItem(row)
                                    }
                                    aria-label="Print"
                                  >
                                    <PrintIcon />
                                  </IconButton>
                                </Box>
                              </TableCell>
                            );
                          }
                          return (
                            <TableCell key={column.id} align={"center"}>
                              {column.format && typeof value === "number"
                                ? column.format(value)
                                : value}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 25, 100]}
              component="div"
              count={tota}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </CardBody>
        </Card>
      </GridItem>
      {/*<Fab color="primary" aria-label="add" className={classes.fab}*/}
      {/*     onClick={() => setOpenAddDialog(true)}>*/}
      {/*    <AddIcon/>*/}
      {/*</Fab>*/}

      {openAddDialog && (
        <AccountAdd
          fetchData={refreshAccounts}
          open={openAddDialog}
          close={closeAddDialog}
        />
      )}
      {selectedItem && openEditDialog ? (
        <AccountEdit
          fetchData={refreshAccounts}
          open={openEditDialog}
          item={selectedItem}
          close={() => {
            setOpenEditDialog(false);
            refreshAccounts(); // Refresh data after edit
          }}
        />
      ) : null}
      {openViewDialog ? (
        <AccountView
          open={openViewDialog.open}
          close={closeViewDialog}
          item={openViewDialog.item}
        />
      ) : null}
      {openConfirmationModal ? (
        <ConfirmationDialog
          open={openConfirmationModal}
          onClose={handleCloseConfirmationModal}
          onConfirm={openConfirmationModal.onPress}
          title={"Delete Record"}
          msg={"Do you want to delete this record?"}
        />
      ) : null}
      <ImportExcel
        db={"accounts"}
        open={openImportDialog}
        close={() => setOpenImportDialog(false)}
      />
      <Backdrop
        open={isExporting}
        style={{
          zIndex: 9999,
          color: '#fff',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
        }}
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
        >
          <CircularProgress size={60} thickness={4} style={{ color: '#512da8', marginBottom: 20 }} />
          <Box style={{ fontSize: '18px', fontWeight: 'bold', marginTop: 10 }}>
            Exporting Data...
          </Box>
          <Box style={{ fontSize: '14px', marginTop: 5, opacity: 0.8 }}>
            Please wait while we prepare your Excel file
          </Box>
        </Box>
      </Backdrop>
    </GridContainer>
  );
}
